'use client';

import { useTranslations } from 'next-intl';
import { useState, type ReactNode } from 'react';
import { AdjustableContactForm } from '@/components/form/AdjustableContactForm';
import { Icon } from '@/components/Icon';
import { SlotRenderer } from '@/components/SlotRenderer';
import { SubmittedCard } from '@/components/SubmittedCard';
import type { LocationDto } from '@/lib/api';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';
import { resolveCenterName } from '@/lib/location/centerName';
import type { ConfirmationEntity } from '@/lib/render/RenderContext';
import {
  RenderContextProvider,
  useOptionalRenderContext,
} from '@/lib/render/RenderContextProvider';
import type { AdjustableContactFormResolved } from '@/src/widgets/adjustable-contact-form/types';

// `onSuccess` always passes the confirmation, but tolerate a missing one so
// a caller that fires the callback bare still reaches the success view.
const EMPTY_CONFIRMATION: ConfirmationEntity = {
  gender: '',
  firstName: '',
  lastName: '',
  plz: '',
};

/**
 * Re-provides the parent render context with `entities.confirmation` set to
 * the just-submitted values — and, when the visitor picked a Standort in the
 * ZipCity autocomplete, `entities.confirmationLocation` with its full DTO —
 * so `{confirmation.gender|firstName|lastName|plz}` and
 * `{confirmationLocation.*}` placeholders resolve inside the
 * `confirmationBody` blocks. Client-side data only, never from the backend
 * or Strapi. Without a parent provider (shared-component usage outside a
 * rendered page) children render as-is; `useSubstitute()` already falls
 * back to identity there.
 */
function ConfirmationEntityProvider({
  confirmation,
  confirmationLocation,
  children,
}: {
  confirmation: ConfirmationEntity;
  confirmationLocation?: LocationDto;
  children: ReactNode;
}) {
  const parentCtx = useOptionalRenderContext();
  if (!parentCtx) {
    return <>{children}</>;
  }
  return (
    <RenderContextProvider
      mode={parentCtx.mode}
      tagBag={parentCtx.tagBag}
      renderable={parentCtx.renderable}
      entities={{
        ...parentCtx.entities,
        confirmation,
        ...(confirmationLocation ? { confirmationLocation } : {}),
      }}
    >
      {children}
    </RenderContextProvider>
  );
}

/**
 * An Adjustable Contact Form plus its success card: after a successful
 * submission the form is replaced by a `SubmittedCard` with the picked
 * Standort's address summary and the CMS `confirmationBody` blocks, with
 * `{confirmation.*}`/`{confirmationLocation.*}` placeholders provided.
 * `submitTracking`/`tracking` fire via `pushCmsEventTracking`. Shared by
 * the `widgets.adjustable-contact-form` widget and the header's
 * `ContactModal`; the caller owns any outer container/layout.
 */
export function AdjustableContactFormWithConfirmation({
  resolved,
  formClassName,
  onSubmitAttempt,
  onSuccess,
}: {
  resolved: AdjustableContactFormResolved;
  formClassName?: string;
  /** Extra pre-send hook, fired once client-side validation passed. */
  onSubmitAttempt?: () => void;
  /** Extra success hook for caller-owned side effects (e.g. GA events). */
  onSuccess?: () => void;
}) {
  const tCommon = useTranslations('common');
  const [confirmation, setConfirmation] = useState<ConfirmationEntity | null>(
    null,
  );
  const [confirmationLocation, setConfirmationLocation] = useState<
    LocationDto | undefined
  >(undefined);

  if (confirmation) {
    return (
      <ConfirmationEntityProvider
        confirmation={confirmation}
        confirmationLocation={confirmationLocation}
      >
        <SubmittedCard>
          {!!resolved.confirmationBody?.length && (
            <div className="mt-10 text-left">
              <SlotRenderer
                blocks={resolved.confirmationBody}
                zoneName="confirmationBody"
              />
            </div>
          )}
          {confirmationLocation && (
            // The Standort the visitor picked in the ZipCity autocomplete
            // — same address summary the Book Appointment success view
            // shows (BookAppointmentWidget.tsx), minus its date/time rows.
            <dl
              className="mx-auto inline-block text-left text-sm"
              data-testid="confirmation-location-summary"
            >
              <div className="flex items-start gap-4">
                <dt className="mt-1 text-gray-500">
                  {/* The pin is the only thing naming this term. */}
                  <Icon
                    src="/icons/map-pin.svg"
                    size={20}
                    label={tCommon('summaryLocation')}
                  />
                </dt>
                <dd>
                  <div>{resolveCenterName(confirmationLocation)}</div>
                  <div>{confirmationLocation.street ?? ''}</div>
                  <div>
                    {confirmationLocation.zipCode &&
                    confirmationLocation.cityName
                      ? `${confirmationLocation.zipCode} ${confirmationLocation.cityName}`
                      : ''}
                  </div>
                </dd>
              </div>
            </dl>
          )}
        </SubmittedCard>
      </ConfirmationEntityProvider>
    );
  }

  return (
    <AdjustableContactForm
      resolved={resolved}
      className={formClassName}
      onSubmitAttempt={() => {
        pushCmsEventTracking(resolved.submitTracking);
        onSubmitAttempt?.();
      }}
      onSuccess={(
        submittedConfirmation?: ConfirmationEntity,
        submittedLocation?: LocationDto,
      ) => {
        pushCmsEventTracking(resolved.tracking);
        onSuccess?.();
        setConfirmationLocation(submittedLocation);
        setConfirmation(submittedConfirmation ?? EMPTY_CONFIRMATION);
      }}
    />
  );
}
